import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import {
  startBillingSetupSession,
  isVercelFeatureBranchDeploy,
  PREVIEW_BILLING_WEBHOOK_NOTICE,
} from "../utils/billingSetup";
import {
  buildDashboardBillingReturnPath,
  loadQuoteResumeDraft,
} from "../utils/billingQuoteResume";

const BillingRequiredModal = ({
  open,
  onClose,
  isOwner = false,
  mechanicId,
  shopId,
  returnPath = "/mechanic-dashboard",
  onSetupComplete,
  gateMessage = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAddPaymentMethod = async () => {
    if (!mechanicId || !shopId) {
      setError("Shop information is still loading. Please try again.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const savedDraft = loadQuoteResumeDraft();
      const effectiveReturnPath =
        savedDraft?.requestId != null
          ? buildDashboardBillingReturnPath(shopId, savedDraft.requestId)
          : returnPath;

      const result = await startBillingSetupSession({
        mechanicId,
        shopId,
        returnPath: effectiveReturnPath,
      });
      if (result.alreadyReady) {
        onSetupComplete?.();
        onClose?.();
      }
    } catch (err) {
      setError(err.message || "Could not start payment setup.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CreditCardIcon color="primary" />
        Payment method required
      </DialogTitle>
      <DialogContent>
        {isVercelFeatureBranchDeploy() && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {PREVIEW_BILLING_WEBHOOK_NOTICE}
          </Alert>
        )}
        {isOwner ? (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {gateMessage ||
                "Add a credit card on file before submitting quotes or confirming jobs. You can stay on the Free plan — a card is required for future platform fees (3% when jobs complete)."}
            </Typography>
            {!gateMessage && (
              <Typography variant="body2" color="text.secondary">
                You are not purchasing a subscription. No platform fee is charged until a
                job is completed.
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body1">
            Your shop needs a payment method on file before submitting quotes. Ask the
            shop owner to add a card in <strong>Billing &amp; Subscriptions</strong> on
            the shop profile.
          </Typography>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {isOwner ? "Not now" : "Close"}
        </Button>
        {isOwner && (
          <Button
            variant="contained"
            onClick={handleAddPaymentMethod}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CreditCardIcon />}
          >
            {loading ? "Redirecting…" : "Add payment method"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BillingRequiredModal;
